import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useProgress } from '../context/ProgressContext';

export default function Login() {
  const { login } = useAuth();
  const { fetchProgress } = useProgress();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [twoFactorToken, setTwoFactorToken] = useState('');
  const [requiresTwoFactor, setRequiresTwoFactor] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [needsVerification, setNeedsVerification] = useState(false);
  const [resendStatus, setResendStatus] = useState('');
  const [searchParams] = useSearchParams();
  const returnUrl = searchParams.get('return');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setNeedsVerification(false);
    setResendStatus('');

    try {
      const success = await login(email, password, twoFactorToken);

      if (success) {
        await fetchProgress();
        if (returnUrl) {
          window.location.href = returnUrl;
        } else {
          navigate('/');
        }
      } else {
        setError('Login failed. Please try again.');
      }
    } catch (err) {
      const data = err.response?.data;

      // New gate: /login returns 403 { requiresVerification: true } for an
      // account that has not clicked its verification link. Persistent block
      // with a resend affordance — this is actionable, not a transient error.
      if (data?.requiresVerification) {
        setNeedsVerification(true);
        return;
      }
      if (data?.requiresTwoFactor) {
        setRequiresTwoFactor(true);
        setError('Please enter your 2FA code');
        return;
      }
      if (err.response?.status === 401) {
        setError('Invalid email or password.');
        return;
      }
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resendVerification = async () => {
    setResendStatus('Sending…');
    try {
      const res = await fetch('/api/auth/resend-verification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      setResendStatus(res.ok
        ? 'Verification email sent. Check your inbox (and spam).'
        : 'Could not send verification email. Try again shortly.');
    } catch {
      setResendStatus('Could not send verification email. Try again shortly.');
    }
  };

  return (
    <div className="max-w-md mx-auto">
      <div className="bg-white rounded-lg shadow-md p-8">
        <h1 className="text-2xl font-bold mb-2">Login to Our Pickup</h1>
        <p className="text-gray-600 mb-6">Welcome back! Login to your account.</p>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {needsVerification && (
          <div className="bg-yellow-100 border border-yellow-400 text-yellow-800 px-4 py-3 rounded mb-4">
            <p className="mb-2">
              Your email address is not verified. Check your inbox for the
              verification link before logging in.
            </p>
            <button
              type="button"
              onClick={resendVerification}
              className="underline text-yellow-900 font-semibold"
            >
              Resend verification email
            </button>
            {resendStatus && <p className="mt-2 text-sm">{resendStatus}</p>}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-gray-700 mb-2">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full border rounded-md px-3 py-2"
              required
            />
          </div>

          <div>
            <label className="block text-gray-700 mb-2">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full border rounded-md px-3 py-2"
              required
            />
          </div>

          {requiresTwoFactor && (
            <div>
              <label className="block text-gray-700 mb-2">2FA Code</label>
              <input
                type="text"
                value={twoFactorToken}
                onChange={(e) => setTwoFactorToken(e.target.value)}
                className="w-full border rounded-md px-3 py-2"
                placeholder="Enter 2FA code"
                required
              />
              <p className="text-xs text-gray-400 mt-1">Enter the 6-digit code from your authenticator app</p>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>

        <div className="mt-4 text-center space-y-2">
          <Link to="/register" className="text-blue-500 hover:text-blue-700 text-sm block">
            Don't have an account? Register
          </Link>
          <Link to="/forgot-password" className="text-gray-500 hover:text-gray-700 text-sm block">
            Forgot password?
          </Link>
        </div>
      </div>
    </div>
  );
}
